import type React from "react"
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"

export type ConnectionStatus =
  | "connected"
  | "connecting"
  | "disconnected"
  | "blocked"

interface ConnectionStatusContextType {
  status: ConnectionStatus
  isBlocked: boolean
  lastError: string | null
  retryConnection: () => void
  dismissWarning: () => void
  warningDismissed: boolean
}

const ConnectionStatusContext =
  createContext<ConnectionStatusContextType | null>(null)

export const useConnectionStatus = (): ConnectionStatusContextType => {
  const context = useContext(ConnectionStatusContext)
  if (!context) {
    throw new Error(
      "useConnectionStatus must be used within a ConnectionStatusProvider",
    )
  }
  return context
}

/**
 * Checks if an error is likely caused by an ad blocker
 */
export const isAdBlockerError = (error: unknown): boolean => {
  if (!error) return false

  const errorMessage = error instanceof Error ? error.message : String(error)
  const errorCode =
    error && typeof error === "object" && "code" in error
      ? (error as { code: string }).code
      : ""

  // Common ad blocker error patterns
  const adBlockerPatterns = [
    "ERR_BLOCKED_BY_CLIENT",
    "net::ERR_BLOCKED",
    "Failed to fetch",
    "NetworkError",
    "Load failed",
    // Firestore-specific codes that often indicate blocked connections
    "unavailable",
    "failed-precondition",
  ]

  return (
    adBlockerPatterns.some(
      (pattern) =>
        errorMessage.includes(pattern) || errorCode.includes(pattern),
    ) ||
    // Check for generic network failures to Firestore URLs
    (errorMessage.includes("firestore") && errorMessage.includes("failed"))
  )
}

// Custom event names for connection status reporting
const CONNECTION_ERROR_EVENT = "firebase-connection-error"
const CONNECTION_SUCCESS_EVENT = "firebase-connection-success"

/**
 * Report a connection error from anywhere in the app
 * This dispatches a custom event that ConnectionStatusProvider listens to
 */
export const reportConnectionError = (error: unknown): void => {
  window.dispatchEvent(
    new CustomEvent(CONNECTION_ERROR_EVENT, { detail: { error } }),
  )
}

/**
 * Report a successful connection from anywhere in the app
 */
export const reportConnectionSuccess = (): void => {
  window.dispatchEvent(new CustomEvent(CONNECTION_SUCCESS_EVENT))
}

/**
 * Performs a lightweight connection test to Firestore
 */
const testFirestoreConnection = async (projectId: string): Promise<boolean> => {
  try {
    // Use a simple fetch to test if requests to Firestore are blocked
    // We use a HEAD request which is lightweight and will fail if blocked
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 5000)

    await fetch(
      `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents`,
      {
        method: "HEAD",
        mode: "no-cors", // Allow the request even with CORS
        signal: controller.signal,
      },
    )

    clearTimeout(timeoutId)
    return true
  } catch (error) {
    // Check if it's specifically a network error (blocked by client)
    return !isAdBlockerError(error)
  }
}

interface ConnectionStatusProviderProps {
  children: ReactNode
  projectId?: string
}

export const ConnectionStatusProvider: React.FC<
  ConnectionStatusProviderProps
> = ({ children, projectId }) => {
  const [status, setStatus] = useState<ConnectionStatus>("connecting")
  const [lastError, setLastError] = useState<string | null>(null)
  const [warningDismissed, setWarningDismissed] = useState(false)
  const errorCountRef = useRef(0)
  const lastCheckRef = useRef<number>(0)
  const successReceivedRef = useRef(false)

  // Check if warning was previously dismissed in this session
  useEffect(() => {
    const dismissed = sessionStorage.getItem("connection-warning-dismissed")
    if (dismissed === "true") {
      setWarningDismissed(true)
    }
  }, [])

  // Listen for connection error events
  useEffect(() => {
    const handleError = (event: Event) => {
      const customEvent = event as CustomEvent<{ error: unknown }>
      const error = customEvent.detail?.error

      errorCountRef.current += 1
      const errorMessage =
        error instanceof Error ? error.message : String(error)
      setLastError(errorMessage)

      if (isAdBlockerError(error)) {
        setStatus("blocked")
      } else if (errorCountRef.current >= 3) {
        // Multiple non-ad-blocker errors suggest general connectivity issues
        setStatus("disconnected")
      }
    }

    const handleSuccess = () => {
      // Only update to connected if we've received success events
      successReceivedRef.current = true
      errorCountRef.current = 0
      setLastError(null)
      setStatus("connected")
    }

    window.addEventListener(CONNECTION_ERROR_EVENT, handleError)
    window.addEventListener(CONNECTION_SUCCESS_EVENT, handleSuccess)

    return () => {
      window.removeEventListener(CONNECTION_ERROR_EVENT, handleError)
      window.removeEventListener(CONNECTION_SUCCESS_EVENT, handleSuccess)
    }
  }, [])

  /**
   * Retry connection test
   */
  const retryConnection = useCallback(async () => {
    // Prevent rapid retries
    const now = Date.now()
    if (now - lastCheckRef.current < 5000) return
    lastCheckRef.current = now

    setStatus("connecting")
    errorCountRef.current = 0

    if (projectId) {
      const isConnected = await testFirestoreConnection(projectId)
      if (isConnected) {
        setStatus("connected")
        setLastError(null)
        // Reload the page to re-establish Firestore connections
        window.location.reload()
      } else {
        setStatus("blocked")
        setLastError("Connection to Firebase appears to be blocked")
      }
    } else {
      // No projectId, just reload
      window.location.reload()
    }
  }, [projectId])

  /**
   * Dismiss the warning for this session
   */
  const dismissWarning = useCallback(() => {
    setWarningDismissed(true)
    sessionStorage.setItem("connection-warning-dismissed", "true")
  }, [])

  // Initial connection check after a short delay
  // This gives Firestore listeners time to establish connections
  useEffect(() => {
    const checkTimer = setTimeout(() => {
      // If no success events received and we have a projectId, test the connection
      if (!successReceivedRef.current && projectId) {
        testFirestoreConnection(projectId).then((isConnected) => {
          if (isConnected && status === "connecting") {
            // Connection test passed but no Firestore events yet
            // Keep waiting a bit longer
            setTimeout(() => {
              if (!successReceivedRef.current && status === "connecting") {
                // Still no events, assume connected (Firestore may just be idle)
                setStatus("connected")
              }
            }, 3000)
          } else if (!isConnected) {
            setStatus("blocked")
            setLastError("Connection to Firebase appears to be blocked")
          }
        })
      } else if (!projectId && status === "connecting") {
        // No projectId provided, assume connected
        setStatus("connected")
      }
    }, 2000)

    return () => clearTimeout(checkTimer)
  }, [projectId, status])

  const value: ConnectionStatusContextType = {
    status,
    isBlocked: status === "blocked",
    lastError,
    retryConnection,
    dismissWarning,
    warningDismissed,
  }

  return (
    <ConnectionStatusContext.Provider value={value}>
      {children}
    </ConnectionStatusContext.Provider>
  )
}

/**
 * Hook for Firebase listeners to report connection status
 * @deprecated Use reportConnectionError and reportConnectionSuccess directly
 */
export const useConnectionReporter = () => {
  return {
    reportError: reportConnectionError,
    reportSuccess: reportConnectionSuccess,
  }
}
